import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { DeleteIcon, InfoIcon, LinkIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import useLoanId from 'hooks/useLoanId';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

import {
  openConfirmationDialogAction,
  openDialogAction,
  showMessageAction,
} from 'store/global/actions';
import {
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
  removeConditionTaskAction,
  unlinkConditionTaskAction,
} from 'store/loanApplications/actions';

import { TaskSlaGroupInfoModal } from '../SlaGroupInfo';

import { ICON_SIZE } from './TaskRowActionButton.constants';

const TaskRowActionButton = ({ task, conditionId, canRemove = false }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId();
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();
  const [isActionLoading, setIsActionLoading] = useState(false);

  const { slaGroupId, name, taskId } = task;

  const onOpenSlaLevelDetails = useCallback(() => {
    dispatch(
      openDialogAction({
        size: ModalSize.XL,
        header: `${name} SLA Group Info`,
        body: dialogProps => <TaskSlaGroupInfoModal slaGroupId={slaGroupId} {...dialogProps} />,
      }),
    );
  }, [dispatch, slaGroupId, name]);

  const refreshConditionAndTasks = useCallback(
    () =>
      Promise.all([
        dispatch(getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true })),
        dispatch(getLoanApplicationTasksAction({ applicationId })),
      ]),
    [applicationId, dispatch, loanId],
  );

  const runTaskAction = useCallback(
    ({ action, successMessage }) => {
      setIsActionLoading(true);

      dispatch(action({ loanConditionId: conditionId, taskId }))
        .then(refreshConditionAndTasks)
        .then(() => dispatch(showMessageAction({ message: successMessage, variant: 'success' })))
        .finally(() => setIsActionLoading(false));
    },
    [conditionId, dispatch, refreshConditionAndTasks, taskId],
  );

  const onUnlink = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        message: `Unlink task "${name}" from this condition?`,
        description: 'The task stays on the loan; it just no longer counts toward this condition.',
        type: ConfirmationModalType.WARNING,
        onAcceptButtonName: 'Unlink',
        onCancelButtonName: 'Cancel',
        onAccept: () =>
          runTaskAction({ action: unlinkConditionTaskAction, successMessage: 'Task unlinked.' }),
      }),
    );
  }, [dispatch, name, runTaskAction]);

  const onRemove = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        message: `Remove task "${name}" from this condition?`,
        description: 'The task is also deleted from the loan. This action cannot be undone.',
        type: ConfirmationModalType.WARNING,
        onAcceptButtonName: 'Remove',
        onCancelButtonName: 'Cancel',
        onAccept: () =>
          runTaskAction({ action: removeConditionTaskAction, successMessage: 'Task removed.' }),
      }),
    );
  }, [dispatch, name, runTaskAction]);

  const items = useMemo(
    () => [
      ...getConditionalArray(!!slaGroupId, () => [
        {
          text: 'SLA Level Details',
          onClick: onOpenSlaLevelDetails,
          icon: <InfoIcon size={ICON_SIZE} />,
        },
      ]),
      ...getConditionalArray(canRemove && !!taskId, () => [
        {
          text: 'Unlink from Condition',
          onClick: onUnlink,
          icon: <LinkIcon size={ICON_SIZE} />,
          isDisabled: isActionLoading,
        },
        {
          text: 'Remove',
          onClick: onRemove,
          icon: <DeleteIcon size={ICON_SIZE} />,
          isDisabled: isActionLoading,
        },
      ]),
    ],
    [canRemove, isActionLoading, taskId, onOpenSlaLevelDetails, onRemove, onUnlink, slaGroupId],
  );

  if (!items.length) {
    return null;
  }

  return (
    <>
      <MoreButtonCDS
        disabled={isActionLoading}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />
      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

TaskRowActionButton.propTypes = {
  task: PropTypes.object.isRequired,
  conditionId: PropTypes.string.isRequired,
  canRemove: PropTypes.bool,
};

export default memo(TaskRowActionButton);
